// Core fitting utilities converted from the original TypeScript implementation.
// Computes the best linear segment in stress-strain data for yield estimation.

function regressionFromPrefix(prefix, start, end) {
  const n = end - start + 1;
  if (n < 2) return null;
  const idxStart = start;
  const idxEnd = end + 1;

  const sumX = prefix.sumX[idxEnd] - prefix.sumX[idxStart];
  const sumY = prefix.sumY[idxEnd] - prefix.sumY[idxStart];
  const sumX2 = prefix.sumX2[idxEnd] - prefix.sumX2[idxStart];
  const sumY2 = prefix.sumY2[idxEnd] - prefix.sumY2[idxStart];
  const sumXY = prefix.sumXY[idxEnd] - prefix.sumXY[idxStart];

  const denom = n * sumX2 - sumX * sumX;
  if (Math.abs(denom) < 1e-12) return null;

  const slope = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;

  const meanY = sumY / n;
  const sst = sumY2 - sumY * meanY;

  const sse =
    sumY2 +
    n * intercept * intercept +
    slope * slope * sumX2 -
    2 * intercept * sumY -
    2 * slope * sumXY +
    2 * intercept * slope * sumX;

  const mse = sse / n;
  const r2 = sst === 0 ? 1 : Math.max(0, 1 - sse / sst);

  return { slope, intercept, r2, mse };
}

export function computeBestSegment(data, r2Target, minWindow) {
  const n = data.length;
  const minWin = Math.max(5, minWindow);

  if (n < minWin) return null;

  const sumX = new Float64Array(n + 1);
  const sumY = new Float64Array(n + 1);
  const sumX2 = new Float64Array(n + 1);
  const sumY2 = new Float64Array(n + 1);
  const sumXY = new Float64Array(n + 1);

  for (let i = 0; i < n; i++) {
    const p = data[i];
    sumX[i + 1] = sumX[i] + p.strain;
    sumY[i + 1] = sumY[i] + p.stress;
    sumX2[i + 1] = sumX2[i] + p.strain * p.strain;
    sumY2[i + 1] = sumY2[i] + p.stress * p.stress;
    sumXY[i + 1] = sumXY[i] + p.strain * p.stress;
  }

  const prefix = { sumX, sumY, sumX2, sumY2, sumXY };
  const searchLimit = Math.floor(n * 0.3);

  let globalBest = null;
  let maxLength = 0;

  let start = 0;
  while (start < searchLimit) {
    const checkEnd = start + minWin - 1;
    if (checkEnd >= n) break;

    const checkReg = regressionFromPrefix(prefix, start, checkEnd);
    if (!checkReg || checkReg.slope <= 0 || checkReg.r2 < 0.98) {
      start++;
      continue;
    }

    let end = checkEnd;
    let maxFoundSlope = checkReg.slope;
    let currentSegmentBestEnd = end;
    let currentSegmentReg = checkReg;
    const SLOPE_DROP_THRESHOLD = 0.05;

    while (end < n - 1) {
      const nextEnd = end + 1;
      const reg = regressionFromPrefix(prefix, start, nextEnd);
      if (!reg || reg.slope <= 0) break;

      if (reg.slope > maxFoundSlope) {
        maxFoundSlope = reg.slope;
      }

      const dropRatio = (maxFoundSlope - reg.slope) / maxFoundSlope;
      const isElastic = reg.r2 >= r2Target && dropRatio <= SLOPE_DROP_THRESHOLD;

      if (isElastic) {
        currentSegmentBestEnd = nextEnd;
        currentSegmentReg = reg;
        end = nextEnd;
      } else {
        break;
      }
    }

    const len = currentSegmentBestEnd - start;
    if (len > maxLength) {
      maxLength = len;
      globalBest = { start, end: currentSegmentBestEnd, regression: currentSegmentReg };
    }

    // Only early-stop if the found segment has R² above the target threshold
    if (maxLength > 50 && globalBest && globalBest.regression.r2 >= r2Target) break;
    // Skip past the current segment to avoid re-scanning similar regions
    start = currentSegmentBestEnd + 1;
  }

  return globalBest;
}
